/*
 * Copyright (c) 2022 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import {Injectable} from '@angular/core';
import {EnvironmentService} from "@irian-ro/util-environment";
import { HttpClient } from '@angular/common/http';
import {Observable} from 'rxjs';
import { AbstractResourceService, JsonApiListResourceMetadataBase, JsonApiResource, QueryParams } from '@irian-ro/abstract-resource';
import {map} from 'rxjs/operators';
import {
  BusinessProfileInfoVO,
  BusinessProfileListModel,
  ContactListMetadata,
  ContactListVO,
  PartnerBusinessProfileSummaryListVo
} from '@well/domain';

@Injectable({
  providedIn: 'root'
})
export class BusinessProfileResourceService extends AbstractResourceService<any, undefined, undefined, undefined> {

  constructor(protected httpClient: HttpClient,
              private environmentService: EnvironmentService) {
    super(httpClient, environmentService.getEnvironmentProperty('companyBusinessProfileUrl'), undefined, undefined,undefined, undefined);
  }

  getPublicBusinessProfiles(query?: any, additionalParams?: any): Observable<JsonApiResource<BusinessProfileListModel[], any>> {
    const queryString = new QueryParams(
      query,
      additionalParams ? additionalParams : []
    ).toString();
    return this.httpClient.get<JsonApiResource<BusinessProfileListModel[], any>>(`${this.url}/public${queryString}`).pipe(
      map((jsonApiResource) => {
        jsonApiResource.data = jsonApiResource.data.map((item: any) => AbstractResourceService.resourceMapper(this.vType, item)
        );
        jsonApiResource.meta = AbstractResourceService.resourceMapper(
          this.mvType,
          jsonApiResource.meta
        );
        return jsonApiResource;
      })
    );
  }

  getPartnersBusinessProfiles(query?: any, additionalParams?: any): Observable<JsonApiResource<BusinessProfileListModel[], any>> {
    const queryString = new QueryParams(
      query,
      additionalParams ? additionalParams : []
    ).toString();
    return this.httpClient.get<JsonApiResource<BusinessProfileListModel[], any>>(`${this.url}/partners${queryString}`).pipe(
      map((jsonApiResource) => {
        jsonApiResource.data = jsonApiResource.data.map((item: any) => AbstractResourceService.resourceMapper(this.vType, item)
        );
        jsonApiResource.meta = AbstractResourceService.resourceMapper(
          this.mvType,
          jsonApiResource.meta
        );
        return jsonApiResource;
      })
    );
  }

  getPartnersContacts(query?: any, additionalParams?: any): Observable<JsonApiResource<ContactListVO[], ContactListMetadata>> {
    const queryString = new QueryParams(
      query,
      additionalParams ? additionalParams : []).toString();
    return this.httpClient.get<JsonApiResource<ContactListVO[], ContactListMetadata>>(`${this.url}/partners/contacts${queryString}`).pipe(
      map((jsonApiResource) => {
        jsonApiResource.data = jsonApiResource.data.map((item: any) => AbstractResourceService.resourceMapper(ContactListVO, item)
        );
        jsonApiResource.meta = AbstractResourceService.resourceMapper(
          ContactListMetadata,
          jsonApiResource.meta
        );
        return jsonApiResource;
      })
    );
  }

  getPublicContactsForPublicBusinessProfile(publicBusinessProfileUuid: string, query?: any, additionalParams?: any): Observable<JsonApiResource<ContactListVO[], ContactListMetadata>> {
    const queryString = new QueryParams(
      query,
      additionalParams ? additionalParams : []
    ).toString();
    return this.httpClient.get<JsonApiResource<ContactListVO[], ContactListMetadata>>(`${this.url}/public/contacts/${publicBusinessProfileUuid}${queryString}`).pipe(
      map((jsonApiResource) => {
        jsonApiResource.data = jsonApiResource.data.map((item: any) => AbstractResourceService.resourceMapper(ContactListVO, item)
        );
        jsonApiResource.meta = AbstractResourceService.resourceMapper(
          ContactListMetadata,
          jsonApiResource.meta
        );
        return jsonApiResource;
      })
    );
  }

  getContactForPartnerBusinessProfile(partnerBusinessProfileUuid: string, query?: any, additionalParams?: any): Observable<JsonApiResource<ContactListVO[], ContactListMetadata>> {
    const queryString = new QueryParams(
      query,
      additionalParams ? additionalParams : []
    ).toString();
    return this.httpClient.get<JsonApiResource<ContactListVO[], ContactListMetadata>>(`${this.url}/partner/contacts/${partnerBusinessProfileUuid}${queryString}`).pipe(
      map((jsonApiResource) => {
        jsonApiResource.data = jsonApiResource.data.map((item: any) => AbstractResourceService.resourceMapper(ContactListVO, item)
        );
        jsonApiResource.meta = AbstractResourceService.resourceMapper(
          ContactListMetadata,
          jsonApiResource.meta
        );
        return jsonApiResource;
      })
    );
  }

  getAddressesForCompany(businessProfileUuid: string, query?: any, additionalParams?: any): Observable<JsonApiResource<any[], any>> {
    const queryString = new QueryParams(
      query,
      additionalParams ? additionalParams : []).toString();
    return this.httpClient.get<JsonApiResource<any[], any>>(`${this.url}/addresses/${businessProfileUuid}${queryString}`).pipe(
      map((jsonApiResource) => {
        jsonApiResource.data = jsonApiResource.data.map((item: any) => AbstractResourceService.resourceMapper(undefined, item)
        );
        jsonApiResource.meta = AbstractResourceService.resourceMapper(
          undefined,
          jsonApiResource.meta
        );
        return jsonApiResource;
      })
    );
  }

  getPublicAddressesForCompany(businessProfileUuid: string, query?: any, additionalParams?: any): Observable<JsonApiResource<any[], any>> {
    const queryString = new QueryParams(
      query,
      additionalParams ? additionalParams : []
    ).toString();
    return this.httpClient.get<JsonApiResource<any[], any>>(`${this.url}/public/addresses/${businessProfileUuid}${queryString}`).pipe(
      map((jsonApiResource) => {
        jsonApiResource.data = jsonApiResource.data.map((item: any) => AbstractResourceService.resourceMapper(undefined, item)
        );
        jsonApiResource.meta = AbstractResourceService.resourceMapper(
          undefined,
          jsonApiResource.meta
        );
        return jsonApiResource;
      })
    );
  }

  getCustomerPartnerBusinessProfilesForCurrentOwner(): Observable<JsonApiResource<PartnerBusinessProfileSummaryListVo[], JsonApiListResourceMetadataBase>> {
    return this.httpClient.get(`${this.url}/partners/customer`).pipe(this.getDtoMapper());
  }

  getVendorPartnerBusinessProfilesForCurrentOwner(): Observable<JsonApiResource<PartnerBusinessProfileSummaryListVo[], JsonApiListResourceMetadataBase>> {
    return this.httpClient.get(`${this.url}/partners/vendor`).pipe(this.getDtoMapper());
  }

  getBusinessProfileInfo(uuid: string): Observable<JsonApiResource<BusinessProfileInfoVO, JsonApiListResourceMetadataBase>> {
    return this.httpClient.get(`${this.url}/info/${uuid}`).pipe(this.getDtoMapper());
  }

  getPartnerBusinessProfileInfo(ownerLeUuid: string, partnerUuid: string){
    return this.httpClient.get(`${this.url}/info/owner/${ownerLeUuid}/partner/${partnerUuid}`).pipe(this.getDtoMapper());
  }

  getPublicBusinessProfileInfoForOwner(ownerLeUuid: string): Observable<JsonApiResource<BusinessProfileInfoVO, JsonApiListResourceMetadataBase>> {
    return this.httpClient.get(`${this.url}/find-public-by-owner/${ownerLeUuid}`).pipe(this.getDtoMapper());
  }

  getOrSaveBusinessProfilesWithPartner(partnerLegalEntityUuid: string | null): Observable<JsonApiResource<PartnerBusinessProfileSummaryListVo, null>> {
    return this.httpClient.post(`${this.url}/partner/${partnerLegalEntityUuid}`, null).pipe(this.getDtoMapper());
  }
}

